import {Input, Space} from "@ui";
import {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import styles from "./email-input.styles.module.scss";

const EMAIL_DOMAINS = [
  "gmail.com",
  "hotmail.com",
  "yahoo.com",
  "outlook.com",
  "live.com",
  "icloud.com",
  "protonmail.com",
  "aol.com",
];

const DEFAULT_DOMAIN = "gmail.com";
const sanitize = s => s.replace(/@/g, "").replace(/[^a-zA-Z0-9._-]/g, "");

export const EmailInput = ({value = "", onChange, placeholder, style}) => {
  const {t} = useTranslation();
  const [domain, setDomain] = useState(DEFAULT_DOMAIN);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({top: 0, left: 0, width: 0});
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const at = value.indexOf("@");
  const valueDomain = at > -1 ? value.slice(at + 1) : "";
  const isOther = value.endsWith("@") || (Boolean(valueDomain) && !EMAIL_DOMAINS.includes(valueDomain));
  const localPart =
    isOther && !value.endsWith("@")
      ? value
      : at > -1
        ? sanitize(value.slice(0, at))
        : EMAIL_DOMAINS.includes(value)
          ? ""
          : "";

  useEffect(() => {
    if (EMAIL_DOMAINS.includes(valueDomain)) {
      setDomain(valueDomain);
    }
  }, [valueDomain]);

  useEffect(() => {
    const close = e => {
      if (!buttonRef.current?.contains(e.target) && !dropdownRef.current?.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    if (!dropdownOpen || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setDropdownPosition({top: rect.bottom + 4, left: rect.right - 160, width: 160});
  }, [dropdownOpen]);

  const emit = (local, dom = domain) => onChange?.(local ? `${local}@${dom}` : "");

  const handleLocalChange = e => {
    if (isOther) {
      onChange?.(e.target.value);
      return;
    }
    emit(sanitize(e.target.value));
  };

  const handleDomainChange = newDomain => {
    setDropdownOpen(false);
    if (newDomain === "other") {
      onChange?.(localPart && !isOther ? `${localPart}@` : "");
      return;
    }
    setDomain(newDomain);
    if (!isOther) {
      emit(localPart, newDomain);
    }
  };

  return (
    <Space.Compact
      block
      className={styles.email_input_group}
    >
      <Input
        className={styles.ant_input}
        placeholder={isOther ? t("loginEmailPlaceholder") : placeholder}
        value={isOther ? value : localPart}
        onChange={handleLocalChange}
      />
      {!isOther && (
        <span
          className={styles.email_at_separator}
          aria-hidden="true"
        >
          @
        </span>
      )}
      <div
        className={`${styles.domain_select_wrapper} ${isOther ? styles.domain_select_wrapper_other : ""}`}
        style={{...style, flex: isOther && !dropdownOpen ? 0.3 : 1}}
      >
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className={styles.domain_select}
        >
          {isOther ? t("loginEmailDomainOther") : domain}
        </button>
        {dropdownOpen &&
          createPortal(
            <div
              ref={dropdownRef}
              className={styles.domain_dropdown_container}
              style={{
                position: "fixed",
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
              }}
            >
              <ul className={styles.domain_dropdown}>
                {EMAIL_DOMAINS.map(d => (
                  <li key={d}>
                    <button
                      type="button"
                      className={styles.domain_dropdown_option}
                      onClick={() => handleDomainChange(d)}
                    >
                      {d}
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    type="button"
                    className={styles.domain_dropdown_option}
                    onClick={() => handleDomainChange("other")}
                  >
                    {t("loginEmailDomainOther")}
                  </button>
                </li>
              </ul>
            </div>,
            document.body
          )}
      </div>
    </Space.Compact>
  );
};
